import { useState, useEffect, useRef } from 'react';
import InfoHint from '../text/InfoHint';
import { T } from '../../components/text/TextEdit';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { Check, Copy, Loader2, AlertTriangle, CheckCircle2, AlertCircle } from 'lucide-react';
import { SourceBadge } from './charts';

const COPIED_RESET_MS = 1600;

// One claim, one rendering. This printed the raw value with its own colours while SourceBadge
// printed prose for the same thing; now it IS SourceBadge and keeps only the test hook.
// Falls back to 'modeled-not-fact' - an unlabelled number must never read as a measured one.
export function ProvenanceLabel({ value }) {
  return <SourceBadge source={value || 'modeled-not-fact'} testId="provenance-label" />;
}

export function Disclaimer({ children, testId = 'no-corpus-disclaimer' }) {
  return (
    <div
      className="flex items-start gap-3 rounded-md border border-warning-border bg-warning-soft p-4 text-foreground dark:bg-warning-soft"
      data-testid={testId}
    >
      <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5 text-warning dark:text-warning" />
      <p className="text-sm leading-relaxed">{children}</p>
    </div>
  );
}

export function CopyButton({ text, label, testId }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const buttonLabel = label || t('ui.copy');
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(text || '');
      setCopied(true);
      setTimeout(() => setCopied(false), COPIED_RESET_MS);
    } catch {
      toast.error(t('ui.copyFailed'));
    }
  };
  return (
    <button
      onClick={onCopy}
      className="flex items-center gap-2 bg-primary text-primary-foreground px-3 py-1.5 rounded-md hover:bg-primary/90 text-sm font-medium transition-colors"
      data-testid={testId}
    >
      {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
      {copied ? t('ui.copied') : buttonLabel}
    </button>
  );
}

// `info` is a BASE key: the hint reads <base>.what / .affects / .example. Three keys rather than one
// blob, so the standard is enforced by the shape instead of by anyone remembering it — see InfoHint.
export function Field({ label, children, mono, info }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs tracking-[0.18em] uppercase font-bold text-muted-foreground inline-flex items-center gap-1.5">
        {label}
        {info && <InfoHint base={info} />}
      </span>
      <div className={`text-foreground leading-relaxed ${mono ? 'font-data text-sm' : ''}`}>
        {children}
      </div>
    </div>
  );
}

export function SectionCard({ title, action, children, testId }) {
  return (
    <section
      className="border border-border bg-card rounded-md p-6 sm:p-8 flex flex-col gap-6"
      data-testid={testId}
    >
      {(title || action) && (
        <div className="flex items-start justify-between gap-4">
          {title && <h3 className="font-heading text-xl font-semibold text-foreground">{title}</h3>}
          {action}
        </div>
      )}
      {children}
    </section>
  );
}

export function Spinner({ label }) {
  return (
    <div className="flex items-center gap-2 text-muted-foreground text-sm" data-testid="inline-spinner">
      <Loader2 className="h-4 w-4 animate-spin" />
      {label}
    </div>
  );
}

export function Skeleton({ className = '' }) {
  return <div className={`bg-muted animate-pulse rounded-md ${className}`} />;
}

// Bordered output card: standalone icon + larger-weight title above, content below.
// 1px neutral border, 16px (p-4) internal padding. Used by the Idea & Market output pages.
export function InfoCard({ icon: Icon, title, action, children, className = '', testId }) {
  return (
    <section
      className={`border border-border bg-card rounded-xl p-4 flex flex-col gap-2.5 ${className}`}
      data-testid={testId}
    >
      <div className="flex items-start justify-between gap-3">
        <h3 className="flex items-center gap-2 text-lg font-semibold text-foreground leading-tight">
          {Icon && <Icon className="h-5 w-5 text-muted-foreground shrink-0" />}
          {title}
        </h3>
        {action}
      </div>
      <div className="text-foreground leading-relaxed text-sm">{children ?? '—'}</div>
    </section>
  );
}

function FeatureChip({ text }) {
  const raw = String(text || '');
  const sep = raw.indexOf('—') >= 0 ? raw.indexOf('—') : raw.indexOf(' - ');
  const name = sep > 0 ? raw.slice(0, sep).trim() : raw;
  const desc = sep > 0 ? raw.slice(sep + 1).replace(/^[-—\s]+/, '').trim() : '';
  return (
    <span className="inline-flex flex-col rounded-xl border border-border bg-muted px-3 py-2 text-sm max-w-xs">
      <span className="font-semibold text-foreground leading-snug">{name}</span>
      {desc && <span className="text-muted-foreground text-xs mt-0.5 leading-snug">{desc}</span>}
    </span>
  );
}

export function FeatureChips({ items, testId }) {
  if (!Array.isArray(items) || items.length === 0) return <span className="text-muted-foreground text-sm">—</span>;
  return (
    <div className="flex flex-wrap gap-2" data-testid={testId}>
      {items.map((x, i) => <FeatureChip key={`${x}-${i}`} text={x} />)}
    </div>
  );
}

// Animated generation progress: simulates 0→85% over `durationMs`, holds at 85%, then on
// completion (running → false) jumps to 100% and clears after a beat. A localized status
// message (passed as an array) rotates every 5s with a crossfade.
const STATUS_MSG_INITIAL = { opacity: 0, y: 4 };
const STATUS_MSG_ANIMATE = { opacity: 1, y: 0 };
const STATUS_MSG_EXIT = { opacity: 0, y: -4 };
const STATUS_MSG_TRANSITION = { duration: 0.3 };


/**
 * N-90: the step-by-step list the prototype builder uses (`ScreenProgress`), generalised.
 *
 * Ali asked for THIS shape rather than a bar, and the reason holds up: a bar says "63%" about a
 * batch, which tells you nothing about WHICH file is stuck or how far along it is. Corpus ingest
 * has a real per-file lifecycle — added, uploaded, indexing, indexed — and the honest way to show
 * seventeen of those is seventeen rows that each say where they are, not one number averaging over
 * them. A failure lands on its own row next to its own filename instead of vanishing into a
 * percentage.
 *
 * `items`: [{ id, label, status, note, onRetry }]
 * `status`: done | active | pending | error | warn | idle
 */
export function StepProgressList({ items = [], testId = 'step-progress' }) {
  const badgeFor = (status) => {
    if (status === 'done') return <CheckCircle2 className="h-5 w-5 text-success" />;
    if (status === 'warn') return <AlertCircle className="h-5 w-5 text-warning" />;
    if (status === 'error') return <AlertCircle className="h-5 w-5 text-destructive" />;
    if (status === 'active') return <Loader2 className="h-5 w-5 text-foreground animate-spin" />;
    if (status === 'pending') return <Loader2 className="h-5 w-5 text-muted-foreground animate-spin" />;
    return <span className="h-5 w-5 rounded-full border border-border inline-block" />;
  };
  return (
    <div className="flex flex-col gap-2" data-testid={testId}>
      {items.map((it) => (
        <div
          key={it.id}
          className="flex items-center gap-3 p-3 border border-border rounded-xl bg-muted"
          data-testid={`${testId}-row-${it.id}`}
        >
          <span className="shrink-0 flex items-center">{badgeFor(it.status)}</span>
          <span className="flex-1 min-w-0 text-sm font-medium text-foreground truncate">{it.label}</span>
          {it.note && (
            <span
              className={`text-xs font-data ${it.status === 'error' ? 'text-destructive' : 'text-muted-foreground'}`}
              data-testid={`${testId}-note-${it.id}`}
            >
              {it.note}
            </span>
          )}
        </div>
      ))}
    </div>
  );
}

export function GenerationProgress({ running, messages = [], durationMs = 30000, testId = 'generation-progress' }) {
  const list = Array.isArray(messages) && messages.length ? messages : [];
  const [pct, setPct] = useState(0);
  const [visible, setVisible] = useState(false);
  const [idx, setIdx] = useState(0);
  const rafRef = useRef(null);
  const hideTimer = useRef(null);
  const prevRunning = useRef(false);

  useEffect(() => {
    if (running) {
      prevRunning.current = true;
      if (hideTimer.current) { clearTimeout(hideTimer.current); hideTimer.current = null; }
      setVisible(true);
      setPct(0);
      setIdx(0);
      const start = performance.now();
      const tick = (now) => {
        const target = Math.min(85, ((now - start) / durationMs) * 85);
        setPct(target);
        if (target < 85) rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
      return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
    }
    if (prevRunning.current) {
      prevRunning.current = false;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      setPct(100);
      hideTimer.current = setTimeout(() => setVisible(false), 600);
    }
    return () => { if (hideTimer.current) clearTimeout(hideTimer.current); };
  }, [running, durationMs]);

  useEffect(() => {
    if (!running || list.length <= 1) return undefined;
    const id = setInterval(() => setIdx((i) => (i + 1) % list.length), 5000);
    return () => clearInterval(id);
  }, [running, list.length]);

  if (!visible) return null;
  return (
    <div className="border border-border bg-card rounded-xl p-4 flex flex-col gap-3" data-testid={testId}>
      <div className="h-2 w-full bg-muted rounded-full overflow-hidden">
        <div
          className="h-full bg-primary rounded-full transition-all duration-700 ease-out"
          style={{ width: `${pct}%` }}
          data-testid="progress-bar-fill"
        />
      </div>
      <div className="flex items-center gap-2 text-sm text-muted-foreground min-h-[20px]">
        <Loader2 className="h-4 w-4 animate-spin shrink-0" />
        <AnimatePresence mode="wait">
          <motion.span
            key={idx}
            initial={STATUS_MSG_INITIAL}
            animate={STATUS_MSG_ANIMATE}
            exit={STATUS_MSG_EXIT}
            transition={STATUS_MSG_TRANSITION}
            data-testid="progress-status-message"
          >
            {list[idx] || ''}
          </motion.span>
        </AnimatePresence>
      </div>
    </div>
  );
}

// Compact "v1 · v2 · v3" history switcher. Hidden when there is 0 or 1 version. When an
// older version is selected, a subtle hint tells the user they are viewing history.
export function VersionSwitcher({ versions = [], selected, latest, onSelect, testId = 'version-switcher' }) {
  const { t } = useTranslation();
  if (!Array.isArray(versions) || versions.length <= 1) return null;
  const isOlder = selected != null && latest != null && selected !== latest;
  return (
    <div className="flex flex-col gap-1.5" data-testid={testId}>
      <div className="flex items-center flex-wrap gap-2">
        <span className="text-xs tracking-[0.18em] uppercase font-bold text-muted-foreground me-1"><T k="versions.label" /></span>
        {versions.map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => onSelect(n)}
            aria-pressed={selected === n}
            data-testid={`version-pill-${n}`}
            className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
              selected === n
                ? 'bg-primary text-primary-foreground border-primary'
                : 'bg-card text-muted-foreground border-border hover:border-muted-foreground'
            }`}
          >
            {t('versions.v', { n })}
          </button>
        ))}
      </div>
      {isOlder && (
        <p className="text-xs text-warning" data-testid="version-older-hint">
          {t('versions.viewing', { n: selected, latest })}
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------------------
// ModelCostBar — model override + dry-run round cost, shown ONLY when metering is ON.
//
// This component was REFERENCED by IdeaStage and never existed. The reference sat inside
// `{meteringOn && ( ... )}`, so with metering OFF (the default for an unconfigured install)
// React never evaluated it and nothing broke. The moment metering was switched on it threw a
// ReferenceError during render and white-screened the whole Idea stage. Written here rather
// than in IdeaStage because this is where the shared pipeline UI lives.
//
// Uses only i18n keys that already exist (walletCore.*), so no locale files change and the
// en/fa/es parity check stays green.
// ---------------------------------------------------------------------------------------
export function ModelCostBar({
  models = [],
  selectedModel = '',
  onSelectModel,
  credits = null,
  available = null,
  testId = 'model-cost-bar',
}) {
  const { t } = useTranslation();

  // Credits are the ONLY unit here — no USD hint (no-USD-for-users, Ali 2026-08-06): users buy
  // in Rial and spend credits; the peg is an admin cost-side concern. Tokens never cross this
  // boundary either.
  const short = credits != null && available != null && credits > available;

  return (
    <div
      className="flex items-center gap-x-4 gap-y-2 flex-wrap rounded-md border border-border bg-muted px-3 py-2"
      data-testid={testId}
    >
      <label className="flex items-center gap-2">
        <span className="text-xs font-medium text-muted-foreground"><T k="walletCore.modelPicker" /></span>
        <select
          className="text-xs border border-border rounded-md px-2 py-1 bg-card text-foreground"
          value={selectedModel}
          onChange={(e) => onSelectModel?.(e.target.value)}
          data-testid={`${testId}-select`}
        >
          {/* Empty value = let the server resolve the default. Never auto-pick one here. */}
          <option value=""><T k="walletCore.modelDefault" /></option>
          {models.map((m) => (
            <option key={m.model_id} value={m.model_id}>
              {m.display_label || m.model_id}
            </option>
          ))}
        </select>
      </label>

      {credits != null && (
        <span className="text-xs text-muted-foreground" data-testid={`${testId}-cost`}>
          {t('walletCore.roundCostLabel')}{' '}
          <strong className="font-semibold text-foreground">{credits}</strong>
        </span>
      )}

      {available != null && (
        <span className="text-xs text-muted-foreground" data-testid={`${testId}-available`}>
          {t('walletCore.totalBalances')}: <strong className="font-semibold text-foreground">{available}</strong>
        </span>
      )}

      {short && (
        <span className="flex items-center gap-1 text-xs font-medium text-destructive" data-testid={`${testId}-insufficient`}>
          <AlertTriangle className="h-3.5 w-3.5" />
          {t('walletCore.insufficient')}
        </span>
      )}
    </div>
  );
}
